/**
 * Landing on the jobs list (refresh, link, bookmark), its request waited for
 * the shell (user, permissions, saved filters) and then its lazy chunk — on
 * demo2 it started ~3.5s in. Here the chunk and the last first-page request
 * start during boot, alongside the bootstrap calls.
 *
 * The imports match the `/jobs` route in `components/getRoutes` (it renders
 * one or the other depending on a flag); the query key and fetcher match the
 * page's `useJobsListWithParams`, so the page reads this entry instead of
 * fetching again.
 */
import { queryClient } from '@/lib/queryClient';
import { jobsApi } from '@/api/jobs/jobs.api';
import { jobsKeys } from '@/api/jobs/jobs.keys';
import { readLastJobsListRequest } from '@/pages/ats-jobs/api/lastJobsListRequest';

let requested = false;

export const isJobsListPath = (pathname: string): boolean =>
  pathname === '/jobs' || pathname === '/jobs/';

const preloadChunks = () => {
  const retry = () => {
    // A failed preload must not surface — React.lazy retries on navigation.
    requested = false;
  };
  import('@/pages/ats-jobs').catch(retry);
  import('@/pages/ats-jobs/JobsWithTabs').catch(retry);
};

export const preloadJobsListRoute = (
  location: Pick<Location, 'pathname' | 'search'> = window.location
): void => {
  if (requested || !isJobsListPath(location.pathname)) return;
  requested = true;

  preloadChunks();

  // A searched entry builds a different request, and without a session the
  // call would only bounce off auth.
  if (new URLSearchParams(location.search).has('search')) return;
  try {
    if (!localStorage.getItem('apiv1Auth')) return;
  } catch {
    return;
  }

  const params = readLastJobsListRequest();
  if (!params) return;

  void queryClient.prefetchQuery({
    queryKey: jobsKeys.list(params),
    queryFn: () => jobsApi.list(params),
    // Same as the page's query, so its mount reads this rather than refetching.
    staleTime: 30_000,
  });
};
